import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MailCheck, RefreshCw, Clock, ArrowRight } from "lucide-react";
import axios from "axios";

export default function ResendVerificationBox({ email, onProceedLogin }) {
  const [resending, setResending] = useState(false);
  const [attempts, setAttempts] = useState(0);
  const [cooldown, setCooldown] = useState(29); // Initial 29s timer

  // Countdown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (cooldown > 0 || resending) return;

    setResending(true);
    try {
      await axios.post("/api/auth/resend-verification", { email: email.trim() });
      toast.success("A fresh verification link has been sent to your email!");

      const nextAttempt = attempts + 1;
      setAttempts(nextAttempt);

      // Cooldown rule: 1st retry -> 29s, 2nd retry -> 60s, 3+ retries -> 300s (5 min)
      if (nextAttempt >= 3) {
        setCooldown(300); // 5 minutes lockout
        toast.warning("Maximum resend attempts reached. Please wait 5 minutes.");
      } else if (nextAttempt === 2) {
        setCooldown(60); // 1 minute
      } else {
        setCooldown(29); // 29 seconds
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Failed to resend email. Please try again."
      );
    } finally {
      setResending(false);
    }
  };

  // Format seconds into MM:SS
  const formatTime = (secs) => {
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;
    return `${minutes > 0 ? `${minutes}m ` : ""}${seconds < 10 && minutes > 0 ? `0${seconds}` : seconds}s`;
  };

  return (
    <div className="py-4 text-center space-y-4 animate-in fade-in duration-300">
      <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
        <MailCheck className="w-9 h-9" />
      </div>

      <div>
        <h2 className="text-2xl font-black text-slate-900 tracking-tight">
          Verify Your Email
        </h2>
        <p className="text-sm font-medium text-slate-600 leading-relaxed mt-1">
          We sent a verification link to:
        </p>
      </div>

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-2xl text-xs font-bold text-blue-800 break-all">
        {email}
      </div>

      <p className="text-xs text-slate-500 font-medium">
        Click the link in the email to activate your account. Check your spam folder if it doesn't appear in 1 minute.
      </p>

      {/* Resend Action Section */}
      <div className="pt-2 border-t border-slate-100 flex flex-col items-center gap-2">
        <button
          type="button"
          disabled={cooldown > 0 || resending}
          onClick={handleResend}
          className="inline-flex items-center gap-2 text-xs font-bold text-blue-600 hover:text-blue-700 disabled:text-slate-400 disabled:cursor-not-allowed transition-all py-1.5 px-3 rounded-xl hover:bg-blue-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${resending ? "animate-spin" : ""}`} />
          <span>
            {cooldown > 0
              ? `Resend link in ${formatTime(cooldown)}`
              : "Didn't receive email? Resend Link"}
          </span>
        </button>

        {attempts >= 3 && cooldown > 0 && (
          <div className="flex items-center gap-1 text-[11px] font-semibold text-amber-600 bg-amber-50 px-3 py-1 rounded-lg">
            <Clock className="w-3.5 h-3.5" />
            <span>Rate limit active: please wait {formatTime(cooldown)}</span>
          </div>
        )}
      </div>

      {/* Proceed to Login Button */}
      <button
        type="button"
        onClick={onProceedLogin}
        className="w-full mt-2 py-3.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold rounded-2xl shadow-lg transition-all flex items-center justify-center gap-2"
      >
        <span>Proceed to Login</span>
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}